import React, { useCallback, useState } from 'react';

import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import {
  getIsPausedOrCancelledCategory,
  getNeedsUnparkConfirm,
} from 'src/components/workflow/phase-column-helpers';
import { Apollo } from 'src/gql/generated';

export interface WorkflowColumnChangeColumn {
  category: Apollo.ColumnCategory;
  id: string;
  name: string;
}

export interface ConfirmColumnChangeOptions {
  confirmUnpark: boolean;
}

interface UseWorkflowColumnChangeGuardArgs {
  columns: WorkflowColumnChangeColumn[];
  currentColumn: WorkflowColumnChangeColumn | null | undefined;
  onConfirmColumnChange: (
    columnId: string,
    options: ConfirmColumnChangeOptions
  ) => Promise<void> | void;
  workflowState: Apollo.WorkflowState | null | undefined;
}

export function useWorkflowColumnChangeGuard({
  columns,
  currentColumn,
  onConfirmColumnChange,
  workflowState,
}: UseWorkflowColumnChangeGuardArgs): {
  confirmUnparkMove: () => void;
  dismissSigningLeave: () => void;
  dismissUnpark: () => void;
  handleColumnChange: (columnId: string) => void;
  pendingUnparkColumn: WorkflowColumnChangeColumn | null;
  signingLeaveOpen: boolean;
} {
  const [signingLeaveOpen, setSigningLeaveOpen] = useState(false);
  const [pendingUnparkColumn, setPendingUnparkColumn] = useState<WorkflowColumnChangeColumn | null>(
    null
  );

  const handleColumnChange = useCallback(
    (columnId: string) => {
      if (columnId === currentColumn?.id) return;

      if (workflowState === Apollo.WorkflowState.Signing) {
        setSigningLeaveOpen(true);
        return;
      }

      const targetColumn = columns.find(column => column.id === columnId);
      if (!targetColumn) return;

      const confirmUnpark = Boolean(
        currentColumn && getIsPausedOrCancelledCategory(currentColumn.category)
      );
      if (getNeedsUnparkConfirm(currentColumn?.category, targetColumn.category)) {
        setPendingUnparkColumn(targetColumn);
        return;
      }

      void onConfirmColumnChange(columnId, { confirmUnpark });
    },
    [columns, currentColumn, onConfirmColumnChange, workflowState]
  );

  const dismissSigningLeave = useCallback(() => setSigningLeaveOpen(false), []);
  const dismissUnpark = useCallback(() => setPendingUnparkColumn(null), []);
  const confirmUnparkMove = useCallback(() => {
    if (!pendingUnparkColumn) return;
    void onConfirmColumnChange(pendingUnparkColumn.id, { confirmUnpark: true });
    setPendingUnparkColumn(null);
  }, [onConfirmColumnChange, pendingUnparkColumn]);

  return {
    confirmUnparkMove,
    dismissSigningLeave,
    dismissUnpark,
    handleColumnChange,
    pendingUnparkColumn,
    signingLeaveOpen,
  };
}

interface SigningLeaveDialogProps {
  onClose: () => void;
  open: boolean;
}

export function SigningLeaveDialog({ onClose, open }: SigningLeaveDialogProps): JSX.Element {
  return (
    <ConfirmationDialog
      message="This workflow is out for signature. Revert to draft before moving it to another column."
      open={open}
      primaryText="OK"
      title="Revert to draft first"
      onClickPrimary={onClose}
      onClose={onClose}
    />
  );
}

interface UnparkConfirmDialogProps {
  currentColumnName: string | undefined;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  open: boolean;
  targetColumnName: string | undefined;
}

export function UnparkConfirmDialog({
  currentColumnName,
  onClose,
  onConfirm,
  open,
  targetColumnName,
}: UnparkConfirmDialogProps): JSX.Element {
  const origin = currentColumnName ? `"${currentColumnName}"` : 'its current column';
  const destination = targetColumnName ? `"${targetColumnName}"` : 'the selected column';

  return (
    <ConfirmationDialog
      message={`This workflow is parked in ${origin}. Moving it to ${destination} resumes work.`}
      open={open}
      primaryText="Move and Resume"
      title="Resume this workflow?"
      onClickPrimary={onConfirm}
      onClose={onClose}
    />
  );
}
